import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Plus,
  Edit,
  Eye,
  Shield,
  Search,
  AlertCircle,
  Save,
  X,
} from "lucide-react";
import { cn } from "../lib/utils";
import { useToast } from "../components/ui/use-toast";
import {
  server_post_data,
  get_all_staff_rights,
  action_staff_right,
  save_update_staff_rights,
} from "../ServiceConnection/serviceconnection";
import {
  handleAlphabetRightId,
  handleURLChange_Modify,
} from "../CommonJquery/CommonJquery";

export default function StaffRights() {
  const navigate = useNavigate();
  const { toast } = useToast();

  // State management
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [staffRights, setStaffRights] = useState([]);
  const [filteredRights, setFilteredRights] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  // Dialog states
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isToggleDialogOpen, setIsToggleDialogOpen] = useState(false);
  const [selectedRight, setSelectedRight] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    rightName: "",
    rightType: "1",
    className: "",
    url: "",
    landingPage: "",
  });
  const [errors, setErrors] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState("0");

  // Fetch staff rights
  const fetchStaffRights = useCallback(async () => {
    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("flag", "1");

    try {
      const response = await server_post_data(get_all_staff_rights, fd);

      if (!response.data.error) {
        const rightsData = response.data.message.data || [];
        setStaffRights(rightsData);
        setFilteredRights(rightsData);
        console.log(rightsData);
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error fetching staff rights:", error);
      toast({
        title: "Error",
        description: "Failed to load staff rights",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [toast]);

  // Handle search
  const handleSearch = useCallback(
    (query) => {
      setSearchQuery(query);

      if (!query.trim()) {
        setFilteredRights(staffRights);
        return;
      }

      const searchLower = query.toLowerCase();
      const results = staffRights.filter(
        (right) =>
          right.page_name?.toLowerCase().includes(searchLower) ||
          right.page_url_id?.toLowerCase().includes(searchLower)
      );

      setFilteredRights(results);
    },
    [staffRights]
  );

  // Handle field change
  const handleFieldChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const { [field]: removed, ...rest } = prev;
      return rest;
    });
  }, []);

  // Open create dialog
  const handleOpenCreateDialog = useCallback(() => {
    setEditMode(false);
    setEditId("0");
    setFormData({
      rightName: "",
      rightType: "1",
      className: "",
      url: "",
      landingPage: "",
    });
    setErrors({});
    setIsCreateDialogOpen(true);
  }, []);

  // Open edit dialog
  const handleOpenEditDialog = useCallback(
    async (right) => {
      setEditMode(true);
      setEditId(right.id);

      const fd = new FormData();
      fd.append("call_id", right.id);
      fd.append("flag", "3");

      try {
        const response = await server_post_data(get_all_staff_rights, fd);

        if (!response.data.error && response.data.message.data.length > 0) {
          const rightData = response.data.message.data[0];
          setFormData({
            rightName: rightData.page_name || "",
            rightType: rightData.dropdown ? "1" : "0",
            className: rightData.dropdown ? rightData.page_url_id : "",
            url: !rightData.dropdown ? rightData.page_url_id : "",
            landingPage: rightData.panel_name || "",
          });
          setIsCreateDialogOpen(true);
        }
      } catch (error) {
        console.error("Error fetching right data:", error);
        toast({
          title: "Error",
          description: "Failed to load right data",
          variant: "destructive",
        });
      }
    },
    [toast]
  );

  // Validate form
  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.rightName.trim()) {
      newErrors.rightName = "Permission name is required";
    } else if (formData.rightName.trim().length < 3) {
      newErrors.rightName = "Permission name must be at least 3 characters";
    }

    if (!formData.landingPage) {
      newErrors.landingPage = "Please select a Panel";
    }

    if (formData.rightType === "1") {
      if (!formData.className.trim()) {
        newErrors.className = "Class name is required";
      }
    } else {
      if (!formData.url.trim()) {
        newErrors.url = "URL is required";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Handle save
  const handleSave = useCallback(async () => {
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fill all required fields",
        variant: "destructive",
      });
      return;
    }

    setShowLoaderAdmin(true);

    const fd = new FormData();
    fd.append("main_id", editId);
    fd.append("right_name", formData.rightName.trim());
    fd.append("right_type", formData.rightType);
    fd.append("landing_page", formData.landingPage);
    fd.append(
      "class_name",
      formData.rightType === "1" ? formData.className : formData.url
    );

    try {
      const response = await server_post_data(save_update_staff_rights, fd);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: editMode
            ? "Permission updated successfully"
            : "Permission created successfully",
        });
        setIsCreateDialogOpen(false);
        fetchStaffRights();
      }
    } catch (error) {
      console.error("Save error:", error);
      toast({
        title: "Error",
        description: "Failed to save permission",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [formData, editId, editMode, validateForm, toast, fetchStaffRights]);

  // Handle toggle status
  const handleToggleClick = useCallback((right) => {
    setSelectedRight(right);
    setIsToggleDialogOpen(true);
  }, []);

  const handleConfirmToggle = useCallback(async () => {
    if (!selectedRight) return;

    setShowLoaderAdmin(true);

    const newStatus = selectedRight.flag === "c" ? "d" : "c";

    const fd = new FormData();
    fd.append("id_for_delete", selectedRight.id);
    fd.append("flag_for", "1");
    fd.append("for_status_final", newStatus);

    try {
      const response = await server_post_data(action_staff_right, fd);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: "Status updated successfully",
        });
        fetchStaffRights();
      }
    } catch (error) {
      console.error("Toggle error:", error);
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
      setIsToggleDialogOpen(false);
      setSelectedRight(null);
    }
  }, [selectedRight, toast, fetchStaffRights]);

  // Initial load
  useEffect(() => {
    fetchStaffRights();
  }, [fetchStaffRights]);

  const stats = {
    total: staffRights.length,
    active: staffRights.filter((r) => r.flag === "c").length,
    inactive: staffRights.filter((r) => r.flag === "d").length,
  };

  return (
    <MainLayout>
      <Header
        title="Staff Rights & Permissions"
        subtitle="Manage system access rights and permissions"
      />

      <div className="p-6 space-y-6">
        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.total}
                </p>
                <p className="text-sm text-muted-foreground">
                  Total Permissions
                </p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.active}
                </p>
                <p className="text-sm text-muted-foreground">Active</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.inactive}
                </p>
                <p className="text-sm text-muted-foreground">Inactive</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Actions */}
        <div className="flex flex-col md:flex-row gap-4 justify-between">
          <div className="flex flex-1 gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or URL..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            {searchQuery && (
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery("");
                  setFilteredRights(staffRights);
                }}
              >
                Clear
              </Button>
            )}
          </div>
          <Button
            onClick={handleOpenCreateDialog}
            className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
          >
            <Plus className="w-4 h-4" />
            Add Permission
          </Button>
        </div>

        {/* Rights Table */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground">
                  Permission Name
                </TableHead>
                <TableHead className="text-muted-foreground">URL ID</TableHead>
                <TableHead className="text-muted-foreground">
                  Panel Name
                </TableHead>
                <TableHead className="text-muted-foreground">Status</TableHead>
                <TableHead className="text-muted-foreground text-right">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRights.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="text-center text-muted-foreground"
                  >
                    No permissions found
                  </TableCell>
                </TableRow>
              ) : (
                filteredRights.map((right) => (
                  <TableRow key={right.id} className="border-border">
                    <TableCell className="font-semibold">
                      {right.page_name}
                    </TableCell>
                    <TableCell className="font-mono text-sm text-muted-foreground">
                      {right.page_url_id}
                    </TableCell>
                    <TableCell className="font-mono text-sm text-muted-foreground">
                      {right.panel_name}
                    </TableCell>
                    <TableCell>
                      {console.log(filteredRights)}
                      <Switch
                        checked={right.flag === "c"}
                        onCheckedChange={() => handleToggleClick(right)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenEditDialog(right)}
                          className="hover:bg-primary/10 hover:text-primary"
                          title="Edit Permission"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {right.dropdown ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              navigate(`/view-sub-permissions/${right.id}`)
                            }
                            className="hover:bg-primary/10 hover:text-primary"
                            title="Sub Rights"
                          >
                            <Eye className="w-4 h-4" />
                            <span className="ml-1 text-xs">Sub</span>
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              navigate(`/inside-pagerights/${right.id}`)
                            }
                            className="hover:bg-primary/10 hover:text-primary"
                            title="Inside Page Rights"
                          >
                            <Eye className="w-4 h-4" />
                            <span className="ml-1 text-xs">Inside</span>
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Create/Edit Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>
                {editMode ? "Edit Permission" : "Create New Permission"}
              </DialogTitle>
              <DialogDescription>
                {editMode
                  ? "Update permission details"
                  : "Add a new system permission"}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Permission Name */}
              <div className="space-y-2">
                <Label>
                  Permission Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  placeholder="e.g., Menu Management, User Settings"
                  value={formData.rightName}
                  onChange={(e) =>
                    handleFieldChange("rightName", e.target.value)
                  }
                  className={errors.rightName ? "border-destructive" : ""}
                  minLength={3}
                  maxLength={75}
                />
                {errors.rightName && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.rightName}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Right Type */}
                <div className="space-y-2">
                  <Label>
                    Permission Type <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={formData.rightType}
                    onValueChange={(value) =>
                      handleFieldChange("rightType", value)
                    }
                    disabled={editMode}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Sub-Rights</SelectItem>
                      <SelectItem value="0">No Sub-Rights</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Landing Page */}
                <div className="space-y-2">
                  <Label>
                    Panel <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={formData.landingPage}
                    onValueChange={(value) =>
                      handleFieldChange("landingPage", value)
                    }
                    disabled={editMode}
                  >
                    <SelectTrigger
                      className={errors.landingPage ? "border-destructive" : ""}
                    >
                      <SelectValue placeholder="Select Panel" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="POS">POS</SelectItem>
                      <SelectItem value="Dashboard">Dashboard</SelectItem>
                      <SelectItem value="Mobile Pos">Mobile Pos</SelectItem>
                      <SelectItem value="Manager App">Manager App</SelectItem>
                      <SelectItem value="Captain App">Captain App</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.landingPage && (
                    <p className="text-xs text-destructive flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.landingPage}
                    </p>
                  )}
                </div>
              </div>

              {/* Class Name or URL */}
              {formData.rightType === "1" ? (
                <div className="space-y-2">
                  <Label>
                    Class Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    placeholder="e.g., menu-management"
                    value={formData.className}
                    onChange={(e) => {
                      handleAlphabetRightId(e);
                      handleFieldChange("className", e.target.value);
                    }}
                    className={errors.className ? "border-destructive" : ""}
                    minLength={3}
                    maxLength={75}
                  />
                  {errors.className && (
                    <p className="text-xs text-destructive flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.className}
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>
                    URL <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    placeholder="/menu-management"
                    value={formData.url}
                    onChange={(e) => {
                      handleURLChange_Modify(e);
                      handleFieldChange("url", e.target.value);
                    }}
                    className={errors.url ? "border-destructive" : ""}
                    minLength={3}
                    maxLength={75}
                  />
                  {errors.url && (
                    <p className="text-xs text-destructive flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.url}
                    </p>
                  )}
                </div>
              )}
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={() => setIsCreateDialogOpen(false)}
                disabled={showLoaderAdmin}
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={showLoaderAdmin}
                className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
              >
                {showLoaderAdmin ? (
                  "Saving..."
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    {editMode ? "Update" : "Create"}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Toggle Status Dialog */}
        <AlertDialog
          open={isToggleDialogOpen}
          onOpenChange={setIsToggleDialogOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Change Permission Status</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to{" "}
                {selectedRight?.flag === "c" ? "deactivate" : "activate"} the
                permission <strong>{selectedRight?.page_name}</strong>?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirmToggle}>
                Confirm
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </MainLayout>
  );
}
